import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Trash2, CheckCircle2, Clock, AlertCircle } from 'lucide-react'
import { useInventoryStore } from '../store/inventoryStore'

export default function StockAlertsPage() {
  const navigate = useNavigate()
  const alerts = useInventoryStore((state) => state.alerts)
  const deleteAlert = useInventoryStore((state) => state.deleteAlert)

  const [resolvedAlerts, setResolvedAlerts] = useState([])

  const handleResolveAlert = (id) => {
    setResolvedAlerts([...resolvedAlerts, id])
  }

  const handleDeleteAlert = (id) => {
    deleteAlert(id)
  }

  const activeAlerts = alerts.filter(a => !resolvedAlerts.includes(a.id))
  const criticalAlerts = activeAlerts.filter(a => a.priority === 'CRITICAL')
  const highAlerts = activeAlerts.filter(a => a.priority === 'HIGH')
  const mediumAlerts = activeAlerts.filter(a => a.priority === 'MEDIUM')

  const AlertCard = ({ alert }) => {
    const getPriorityColor = (priority) => {
      switch(priority) {
        case 'CRITICAL': return 'border-red-500/30 bg-red-500/10'
        case 'HIGH': return 'border-orange-500/30 bg-orange-500/10'
        case 'MEDIUM': return 'border-yellow-500/30 bg-yellow-500/10'
        default: return 'border-blue-500/20'
      }
    }

    const getPriorityBadgeColor = (priority) => {
      switch(priority) {
        case 'CRITICAL': return 'bg-red-500/20 text-red-300'
        case 'HIGH': return 'bg-orange-500/20 text-orange-300'
        case 'MEDIUM': return 'bg-yellow-500/20 text-yellow-300'
        default: return 'bg-blue-500/20 text-blue-300'
      }
    }

    return (
      <div key={alert.id} className={`border rounded-lg p-4 transition-all ${getPriorityColor(alert.priority)}`}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-start space-x-3 flex-1">
            {alert.priority === 'CRITICAL' ? (
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
            ) : alert.priority === 'HIGH' ? (
              <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-1" />
            ) : (
              <Clock className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-1" />
            )}
            <div>
              <p className="text-white font-semibold">{alert.item}</p>
              <p className="text-slate-300 text-sm mt-1">{alert.alert}</p>
            </div>
          </div>
          <span className={`text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${getPriorityBadgeColor(alert.priority)}`}>
            {alert.priority}
          </span>
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-slate-500/30">
          <span className="text-xs text-slate-400">{alert.createdAt}</span>
          <div className="flex space-x-2">
            <button
              onClick={() => handleResolveAlert(alert.id)}
              className="text-green-400 hover:text-green-300 p-2 hover:bg-green-500/10 rounded transition-all"
              title="Mark as resolved"
            >
              <CheckCircle2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDeleteAlert(alert.id)}
              className="text-red-400 hover:text-red-300 p-2 hover:bg-red-500/10 rounded transition-all"
              title="Delete alert"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="flex items-center space-x-3">
          <button onClick={() => navigate(-1)} className="text-blue-400 hover:text-blue-300">←</button>
          <h1 className="text-4xl font-bold text-white flex items-center space-x-3">
            <AlertTriangle className="w-8 h-8 text-orange-400" />
            <span>Stock Alerts & Reordering</span>
          </h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-red-500/20 p-6 shadow-lg">
            <p className="text-red-300 text-sm font-semibold mb-2">Critical Alerts</p>
            <p className="text-red-400 text-3xl font-black">{criticalAlerts.length}</p>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-orange-500/20 p-6 shadow-lg">
            <p className="text-orange-300 text-sm font-semibold mb-2">High Priority</p>
            <p className="text-orange-400 text-3xl font-black">{highAlerts.length}</p>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-yellow-500/20 p-6 shadow-lg">
            <p className="text-yellow-300 text-sm font-semibold mb-2">Medium Priority</p>
            <p className="text-yellow-400 text-3xl font-black">{mediumAlerts.length}</p>
          </div>
        </div>

        {/* Critical Alerts */}
        {criticalAlerts.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-red-400 mb-4 flex items-center space-x-2">
              <AlertTriangle className="w-6 h-6" />
              <span>Critical Alerts ({criticalAlerts.length})</span>
            </h2>
            <div className="space-y-3">
              {criticalAlerts.map(alert => <AlertCard key={alert.id} alert={alert} />)}
            </div>
          </div>
        )}

        {/* High Priority Alerts */}
        {highAlerts.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-orange-400 mb-4 flex items-center space-x-2">
              <AlertCircle className="w-6 h-6" />
              <span>High Priority Alerts ({highAlerts.length})</span>
            </h2>
            <div className="space-y-3">
              {highAlerts.map(alert => <AlertCard key={alert.id} alert={alert} />)}
            </div>
          </div>
        )}

        {/* Medium Priority Alerts */}
        {mediumAlerts.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-yellow-400 mb-4 flex items-center space-x-2">
              <Clock className="w-6 h-6" />
              <span>Medium Priority Alerts ({mediumAlerts.length})</span>
            </h2>
            <div className="space-y-3">
              {mediumAlerts.map(alert => <AlertCard key={alert.id} alert={alert} />)}
            </div>
          </div>
        )}

        {/* Empty State */}
        {activeAlerts.length === 0 && (
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-green-500/20 p-12 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-green-400 mb-2">All Clear!</h3>
            <p className="text-slate-400">No active alerts. Your inventory is in good shape.</p>
          </div>
        )}
      </div>
    </div>
  )
}
